import React, { useState, useEffect, useMemo } from 'react';
import { 
  LayoutDashboard, Users, Package, FileText, Settings as SettingsIcon, Plus, Search, Trash2, 
  Save, X, Mail, Phone, DollarSign, ChevronLeft, Filter, Upload, Edit, Download, Database, 
  RefreshCw, Globe, User, CheckCircle, Send, ArrowLeft, ArrowRight, BarChart3, LogOut, Shield, 
  Cloud, FileSpreadsheet, Loader, Copy, Sun, Moon, MessageCircle, FileX, Bell, Clock, 
  AlertCircle, Check, MapPin, Hash, UserCheck, Smartphone, Tag
} from 'lucide-react';
import { THEMES, HOSTINGER_API_URL, CATEGORY_SORT_ORDER, DEFAULT_CATEGORIES, DEFAULT_TERMS, COMPANY_INFO, STATUS_OPTIONS } from '../config/constants';
import { apiCall } from '../services/api';
import BackgroundOrbs from '../components/BackgroundOrbs';
import Card from '../components/Card';
import Button from '../components/Button';
import Badge from '../components/Badge';
import Modal from '../components/Modal';

const LoginView = ({ onLogin, users, isLoading, isConnected, theme }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => { 
    e.preventDefault(); 
    if (!isConnected) {
      setError('Cannot login: Database disconnected. Please check internet connection.');
      return;
    }
    setIsSubmitting(true);
    const res = await apiCall('login', { username, password });
    setIsSubmitting(false);
    if (res && res.success) {
      onLogin(res);
    } else {
      setError(res?.message || 'Invalid credentials');
    }
  };

  const handleForgotPassword = () => { 
    if (!username) { 
      setError('Please enter your username first to reset password.'); 
      return; 
    } 
    const subject = encodeURIComponent(`Password Reset Request: ${username}`); 
    const body = encodeURIComponent(`Hello Team,\n\nPlease reset the password for the Onyx CRM user: ${username}.\n\nThank you.`);
    window.location.href = `mailto:sales@onyxmachinery.com?subject=${subject}&body=${body}`; 
  };

  return (
    <div className={`flex h-screen w-screen items-center justify-center overflow-hidden relative`}>
      <BackgroundOrbs />
      <div className={`w-full max-w-md ${theme === 'dark' ? 'bg-slate-900/60 border-slate-700' : 'bg-white/40 border-white/60'} backdrop-blur-2xl border rounded-[3rem] shadow-[0_20px_60px_-15px_rgba(0,0,0,0.1)] p-12 animate-in zoom-in-95 duration-500 relative z-10`}>
        <div className="text-center mb-10 relative z-10">
          <div className="flex justify-center mb-6">
             <img src="./logo-1.png" alt="Onyx Logo" className="h-24 w-auto object-contain drop-shadow-xl" />
          </div>
          <div className={`text-4xl font-black mb-2 tracking-tighter ${THEMES[theme].heading}`}>ONYX <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-blue-400">CRM</span></div>
          <p className={`${THEMES[theme].textMuted} font-medium`}>Enterprise Management System</p>
          {!isLoading && (
            <div className="mt-6 flex justify-center">
              {isConnected ? (
                <div className="bg-emerald-100/50 text-emerald-600 text-xs py-1.5 px-4 rounded-full inline-flex items-center gap-2 border border-emerald-200 font-bold uppercase tracking-wider backdrop-blur-md">
                    <Database size={12} /> Database Connected
                </div>
              ) : (
                <div className="bg-red-100/50 text-red-600 text-xs py-1.5 px-4 rounded-full inline-flex items-center gap-2 border border-red-200 font-bold uppercase tracking-wider backdrop-blur-md">
                    <AlertCircle size={12} /> Database Disconnected
                </div>
              )}
            </div>
          )}
        </div>
        {isLoading ? (
          <div className="flex flex-col items-center py-10 relative z-10">
            <Loader className="animate-spin text-orange-500 mb-4" size={32} />
            <p className={THEMES[theme].textMuted}>Connecting to Database...</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6 relative z-10">
            <div>
                <label className={`block text-xs font-bold uppercase tracking-wider mb-2 ${THEMES[theme].textMuted} ml-2`}>Username</label>
                <input type="text" className={`w-full p-4 rounded-2xl outline-none ${THEMES[theme].input}`} placeholder="Enter username" value={username} onChange={e => { setUsername(e.target.value); setError(''); }} />
            </div>
            <div>
                <label className={`block text-xs font-bold uppercase tracking-wider mb-2 ${THEMES[theme].textMuted} ml-2`}>Password</label>
                <input type="password" className={`w-full p-4 rounded-2xl outline-none ${THEMES[theme].input}`} placeholder="Enter password" value={password} onChange={e => setPassword(e.target.value)} />
            </div>
            {error && <p className="text-red-500 text-sm text-center bg-red-50/50 p-4 rounded-2xl border border-red-200 font-medium">{error}</p>}
            <button type="submit" disabled={isSubmitting} className={`w-full py-4 bg-gradient-to-r from-blue-600 to-blue-500 text-white font-bold rounded-2xl shadow-xl shadow-blue-500/20 hover:scale-[1.02] transition-all flex justify-center items-center gap-2 disabled:opacity-70 disabled:hover:scale-100`}>
                {isSubmitting ? <><RefreshCw className="animate-spin" size={20} /> Signing in...</> : 'Sign In'}
            </button>
            <button type="button" onClick={handleForgotPassword} className={`w-full text-center text-sm font-medium transition-colors ${theme === 'dark' ? 'text-slate-300 hover:text-orange-400' : 'text-slate-500 hover:text-blue-600'}`}>Forgot Password?</button>
          </form>
        )}
      </div>
    </div>
  );
};

export default LoginView;
